import { useQuery } from '@tanstack/react-query'
import {
  type CanonicalReconciliationRequestPayload,
  type CanonicalCashflowForecastRequestPayload,
  financialIntelligenceService,
  type CashflowForecastRequestPayload,
  type CashflowForecastResponse,
  type FinancialAIEnvelope,
  type FinancialStatementRequestPayload,
  type FinancialStatementResponse,
  type ReconciliationLookup,
  type ReconciliationRequestPayload,
  type ReconciliationResponse,
} from '../services/financialIntelligence'

interface BaseModuleOptions<TPayload> {
  enabled?: boolean
  payload?: TPayload | null
}

interface ReconciliationModuleOptions extends BaseModuleOptions<ReconciliationRequestPayload> {
  source?: 'payload' | 'canonical'
  canonicalPayload?: CanonicalReconciliationRequestPayload | null
  lookup?: ReconciliationLookup
}

interface CashflowModuleOptions extends BaseModuleOptions<CashflowForecastRequestPayload> {
  source?: 'payload' | 'canonical'
  canonicalPayload?: CanonicalCashflowForecastRequestPayload | null
}

interface StatementModuleOptions extends BaseModuleOptions<FinancialStatementRequestPayload> {}

interface UseFinancialIntelligenceOptions {
  enabled?: boolean
  reconciliation?: ReconciliationModuleOptions
  cashflow?: CashflowModuleOptions
  statement?: StatementModuleOptions
}

export function useReconciliationSuggestions(options: ReconciliationModuleOptions = {}) {
  const {
    enabled = true,
    payload,
    canonicalPayload,
    lookup,
    source = 'payload',
  } = options

  const query = useQuery<FinancialAIEnvelope<ReconciliationResponse>>({
    queryKey: ['financial-ai', 'reconciliation', source, payload, canonicalPayload],
    queryFn: () =>
      source === 'canonical'
        ? financialIntelligenceService.getCanonicalReconciliationSuggestions(canonicalPayload ?? {})
        : financialIntelligenceService.getReconciliationSuggestions(payload!),
    enabled: source === 'canonical' ? enabled : enabled && !!payload,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  })

  return {
    ...query,
    lookup,
    reconciliation: query.data?.data,
    audit: query.data?.audit,
  }
}

export function useCashflowForecast(options: CashflowModuleOptions = {}) {
  const {
    enabled = true,
    payload,
    source = 'payload',
    canonicalPayload,
  } = options

  const query = useQuery<FinancialAIEnvelope<CashflowForecastResponse>>({
    queryKey: ['financial-ai', 'cashflow', source, payload, canonicalPayload],
    queryFn: () =>
      source === 'canonical'
        ? financialIntelligenceService.getCanonicalCashflowForecast(canonicalPayload ?? {})
        : financialIntelligenceService.getCashflowForecast(payload!),
    enabled: source === 'canonical' ? enabled : enabled && !!payload,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  })

  return {
    ...query,
    cashflow: query.data?.data,
    audit: query.data?.audit,
  }
}

export function useFinancialStatementAnalysis(options: StatementModuleOptions = {}) {
  const { enabled = true, payload } = options

  const query = useQuery<FinancialAIEnvelope<FinancialStatementResponse>>({
    queryKey: ['financial-ai', 'statements', payload],
    queryFn: () => financialIntelligenceService.analyzeFinancialStatements(payload!),
    enabled: enabled && !!payload,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  })

  return {
    ...query,
    statement: query.data?.data,
    audit: query.data?.audit,
  }
}

export function useFinancialIntelligence(options: UseFinancialIntelligenceOptions = {}) {
  const { enabled = true, reconciliation, cashflow, statement } = options

  const reconciliationQuery = useReconciliationSuggestions({
    enabled: enabled && (reconciliation?.enabled ?? true),
    source: reconciliation?.source,
    payload: reconciliation?.payload,
    canonicalPayload: reconciliation?.canonicalPayload,
    lookup: reconciliation?.lookup,
  })

  const cashflowQuery = useCashflowForecast({
    enabled: enabled && (cashflow?.enabled ?? true),
    source: cashflow?.source,
    payload: cashflow?.payload,
    canonicalPayload: cashflow?.canonicalPayload,
  })

  const statementQuery = useFinancialStatementAnalysis({
    enabled: enabled && (statement?.enabled ?? true),
    payload: statement?.payload,
  })

  return {
    reconciliationQuery,
    cashflowQuery,
    statementQuery,
    reconciliation: reconciliationQuery.reconciliation,
    cashflow: cashflowQuery.cashflow,
    statement: statementQuery.statement,
    isLoading:
      reconciliationQuery.isLoading || cashflowQuery.isLoading || statementQuery.isLoading,
    isFetching:
      reconciliationQuery.isFetching || cashflowQuery.isFetching || statementQuery.isFetching,
    error:
      reconciliationQuery.error ?? cashflowQuery.error ?? statementQuery.error ?? null,
  }
}
